import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { userPreferencesService, UserPreferences, RetailPreferences, TradeFormPreferences, SeasonalityPreferences, MarketClockPreferences, IAAnalysisPreferences, PositionCalculatorPreferences } from '../services/userPreferencesService';
import { useAuth } from './AuthContext';
import i18n from '../i18n';
import { normalizeLanguage, DEFAULT_LANGUAGE } from '../i18n/languages';

interface UserPreferencesContextType {
  preferences: UserPreferences | null;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
  updateTradeFormPreferences: (preferences: Partial<TradeFormPreferences>) => Promise<void>;
  updateRetailPreferences: (preferences: Partial<RetailPreferences>) => Promise<void>;
  updateSeasonalityPreferences: (preferences: Partial<SeasonalityPreferences>) => Promise<void>;
  updateMarketClockPreferences: (preferences: Partial<MarketClockPreferences>) => Promise<void>;
  updateIAAnalysisPreferences: (preferences: Partial<IAAnalysisPreferences>) => Promise<void>;
  updatePositionCalculatorPreferences: (preferences: Partial<PositionCalculatorPreferences>) => Promise<void>;
  loading: boolean;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;

    async function loadPreferences() {
      if (!user) {
        setPreferences(null);
        setLoading(false);
        return;
      }

      try {
        const prefs = await userPreferencesService.getUserPreferences();
        if (mounted) {
          setPreferences(prefs);
          setLoading(false);
        }
      } catch (error) {
        // console.error supprimé
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadPreferences();

    return () => {
      mounted = false;
    };
  }, [user]);

  // Le sélecteur de langue n'écrit que dans le localStorage : sans cette
  // synchronisation, le backend ne saurait pas dans quelle langue rédiger ses
  // réponses IA pour les traitements qui n'ont pas la requête sous la main
  // (mémoire Atlas, titres, travaux différés).
  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const syncAppLanguage = async (rawLanguage: string) => {
      const language = normalizeLanguage(rawLanguage) ?? DEFAULT_LANGUAGE;
      if (cancelled) return;
      try {
        await userPreferencesService.updateUserPreferences({ appLanguage: language });
        if (!cancelled) {
          setPreferences(prev => (prev ? { ...prev, appLanguage: language } : prev));
        }
      } catch {
        // Best effort : la langue part aussi dans chaque requête IA, donc un échec
        // d'écriture ne casse pas les réponses, il ne prive que les traitements
        // hors requête de la préférence à jour.
      }
    };

    void syncAppLanguage(i18n.language);
    i18n.on('languageChanged', syncAppLanguage);

    return () => {
      cancelled = true;
      i18n.off('languageChanged', syncAppLanguage);
    };
  }, [user]);

  const updatePreferences = async (newPreferences: Partial<UserPreferences>) => {
    if (!preferences || !user) return;

    try {
      await userPreferencesService.updateUserPreferences(newPreferences);
      setPreferences(prev => prev ? {
        ...prev,
        ...newPreferences
      } : null);
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  };

  const updateTradeFormPreferences = async (newPreferences: Partial<TradeFormPreferences>) => {
    if (!preferences || !user) return;

    try {
      await userPreferencesService.updateTradeFormPreferences(newPreferences);
      setPreferences(prev => prev ? {
        ...prev,
        tradeForm: {
          ...prev.tradeForm,
          ...newPreferences
        }
      } : null);
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  };

  const updateRetailPreferences = async (newPreferences: Partial<RetailPreferences>) => {
    if (!preferences || !user) return;

    try {
      await userPreferencesService.updateRetailPreferences(newPreferences);
      setPreferences(prev => prev ? {
        ...prev,
        retail: {
          ...prev.retail,
          ...newPreferences
        }
      } : null);
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  };

  const updateSeasonalityPreferences = async (newPreferences: Partial<SeasonalityPreferences>) => {
    if (!preferences || !user) return;

    try {
      await userPreferencesService.updateSeasonalityPreferences(newPreferences);
      setPreferences(prev => prev ? {
        ...prev,
        seasonality: {
          ...prev.seasonality,
          ...newPreferences
        }
      } : null);
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  };

  const updateMarketClockPreferences = async (newPreferences: Partial<MarketClockPreferences>) => {
    if (!preferences || !user) return;

    try {
      await userPreferencesService.updateMarketClockPreferences(newPreferences);
      setPreferences(prev => prev ? {
        ...prev,
        marketClock: {
          ...prev.marketClock,
          ...newPreferences
        }
      } : null);
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  };

  const updateIAAnalysisPreferences = async (newPreferences: Partial<IAAnalysisPreferences>) => {
    if (!preferences || !user) return;

    try {
      await userPreferencesService.updateIAAnalysisPreferences(newPreferences);
      setPreferences(prev => prev ? {
        ...prev,
        iaAnalysis: {
          ...prev.iaAnalysis,
          ...newPreferences
        }
      } : null);
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  };

  const updatePositionCalculatorPreferences = async (newPreferences: Partial<PositionCalculatorPreferences>) => {
    if (!preferences || !user) return;

    try {
      await userPreferencesService.updatePositionCalculatorPreferences(newPreferences);
      setPreferences(prev => prev ? {
        ...prev,
        positionCalculator: {
          ...(prev.positionCalculator || {} as PositionCalculatorPreferences),
          ...newPreferences
        }
      } : null);
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  };

  const value = {
    preferences,
    updatePreferences,
    updateTradeFormPreferences,
    updateRetailPreferences,
    updateSeasonalityPreferences,
    updateMarketClockPreferences,
    updateIAAnalysisPreferences,
    updatePositionCalculatorPreferences,
    loading
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
}
